import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Users, Briefcase, FileText, TrendingUp, Clock,
  Calendar, BarChart2, PieChart, Activity, ArrowUpRight,
  ArrowDownRight, Filter, Download, RefreshCw
} from 'lucide-react';

interface DashboardStats {
  clients: {
    total: number;
    active: number;
    newThisMonth: number;
  };
  matters: {
    total: number;
    active: number;
    newThisMonth: number;
    byType: Record<string, number>;
    byStatus: Record<string, number>;
  };
  documents: {
    total: number;
    generatedThisMonth: number;
    pendingSignature: number;
    byStatus: Record<string, number>;
  };
}

interface RecentActivity {
  id: string;
  type: 'document' | 'client' | 'matter';
  action: string;
  description: string;
  timestamp: Date;
  user: string;
}

export function Dashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [activity, setActivity] = useState<RecentActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<'week' | 'month' | 'quarter' | 'year'>('month');

  useEffect(() => {
    loadDashboardData();
  }, [timeRange]);

  const loadDashboardData = async () => {
    setLoading(true);
    
    // Simulated data - in production, load from Firebase
    await new Promise(resolve => setTimeout(resolve, 500));

    setStats({
      clients: { total: 156, active: 89, newThisMonth: 12 },
      matters: {
        total: 234,
        active: 67,
        newThisMonth: 18,
        byType: {
          real_estate_purchase: 45,
          real_estate_sale: 38,
          estate_planning_trust: 32,
          business_formation: 28,
          other: 91,
        },
        byStatus: {
          active: 67,
          pending_client: 23,
          closing: 15,
          closed: 129,
        },
      },
      documents: {
        total: 1847,
        generatedThisMonth: 156,
        pendingSignature: 34,
        byStatus: {
          draft: 45,
          pending_review: 23,
          approved: 67,
          sent_for_signature: 34,
          complete: 1678,
        },
      },
    });

    setActivity([
      {
        id: '1',
        type: 'document',
        action: 'Generated',
        description: 'Wisconsin Warranty Deed for Smith Property',
        timestamp: new Date(Date.now() - 1000 * 60 * 15),
        user: 'Seth Goettelman',
      },
      {
        id: '2',
        type: 'client',
        action: 'Created',
        description: 'New client: Johnson Family Trust',
        timestamp: new Date(Date.now() - 1000 * 60 * 45),
        user: 'Seth Goettelman',
      },
      {
        id: '3',
        type: 'matter',
        action: 'Status Changed',
        description: 'Matter #2024-0089-REP moved to Closing',
        timestamp: new Date(Date.now() - 1000 * 60 * 120),
        user: 'Seth Goettelman',
      },
      {
        id: '4',
        type: 'document',
        action: 'Signed',
        description: 'Operating Agreement - ABC Holdings LLC',
        timestamp: new Date(Date.now() - 1000 * 60 * 180),
        user: 'Client',
      },
      {
        id: '5',
        type: 'document',
        action: 'Sent',
        description: 'Quitclaim Deed sent to client for signature',
        timestamp: new Date(Date.now() - 1000 * 60 * 240),
        user: 'Seth Goettelman',
      },
    ]);

    setLoading(false);
  };

  const formatTimeAgo = (date: Date) => {
    const minutes = Math.floor((Date.now() - date.getTime()) / 60000);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  const getActivityIcon = (type: RecentActivity['type']) => {
    switch (type) {
      case 'document':
        return <FileText className="w-4 h-4" />;
      case 'client':
        return <Users className="w-4 h-4" />;
      case 'matter':
        return <Briefcase className="w-4 h-4" />;
    }
  };

  if (loading || !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-accent-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <p className="text-primary-400">Practice overview and analytics</p>
        </div>
        
        <div className="flex items-center gap-3">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value as any)}
            className="bg-primary-800 border border-primary-600 text-white rounded-lg px-3 py-2 text-sm"
          >
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="quarter">This Quarter</option>
            <option value="year">This Year</option>
          </select>
          
          <button className="p-2 bg-primary-800 border border-primary-600 rounded-lg hover:bg-primary-700 transition-colors">
            <Download className="w-5 h-5 text-primary-300" />
          </button>
          
          <button 
            onClick={loadDashboardData}
            className="p-2 bg-primary-800 border border-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
          >
            <RefreshCw className="w-5 h-5 text-primary-300" />
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Clients"
          value={stats.clients.total}
          change={stats.clients.newThisMonth}
          changeLabel="new this month"
          icon={<Users className="w-6 h-6" />}
          color="blue"
        />
        <StatCard
          title="Active Matters"
          value={stats.matters.active}
          change={stats.matters.newThisMonth}
          changeLabel="new this month"
          icon={<Briefcase className="w-6 h-6" />}
          color="green"
        />
        <StatCard
          title="Documents Generated"
          value={stats.documents.generatedThisMonth}
          subtitle={`${stats.documents.total} total`}
          icon={<FileText className="w-6 h-6" />}
          color="purple"
        />
        <StatCard
          title="Pending Signatures"
          value={stats.documents.pendingSignature}
          subtitle="awaiting client action"
          icon={<Clock className="w-6 h-6" />}
          color="orange"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Matter Types Chart */}
        <div className="bg-primary-800/50 rounded-xl border border-primary-700 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-semibold text-white flex items-center gap-2">
              <PieChart className="w-5 h-5 text-accent-400" />
              Matters by Type
            </h3>
          </div>
          
          <div className="space-y-3">
            {Object.entries(stats.matters.byType).map(([type, count]) => (
              <div key={type} className="flex items-center gap-3">
                <div className="flex-1">
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-primary-300 capitalize">
                      {type.replace(/_/g, ' ')}
                    </span>
                    <span className="text-white font-medium">{count}</span>
                  </div>
                  <div className="h-2 bg-primary-700 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${(count / stats.matters.total) * 100}%` }}
                      className="h-full bg-accent-500 rounded-full"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Document Status Chart */}
        <div className="bg-primary-800/50 rounded-xl border border-primary-700 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-semibold text-white flex items-center gap-2">
              <BarChart2 className="w-5 h-5 text-accent-400" />
              Document Pipeline
            </h3>
          </div>
          
          <div className="flex items-end justify-between h-48 gap-2">
            {Object.entries(stats.documents.byStatus).map(([status, count], idx) => {
              const maxCount = Math.max(...Object.values(stats.documents.byStatus));
              const height = (count / maxCount) * 100;
              const colors = ['bg-blue-500', 'bg-yellow-500', 'bg-green-500', 'bg-orange-500', 'bg-purple-500'];
              
              return (
                <div key={status} className="flex-1 flex flex-col items-center gap-2">
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: `${height}%` }}
                    className={`w-full rounded-t-lg ${colors[idx % colors.length]}`}
                    style={{ minHeight: '20px' }}
                  />
                  <span className="text-xs text-primary-400 text-center truncate w-full capitalize">
                    {status.replace(/_/g, ' ')}
                  </span>
                  <span className="text-sm font-medium text-white">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-primary-800/50 rounded-xl border border-primary-700 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-semibold text-white flex items-center gap-2">
            <Activity className="w-5 h-5 text-accent-400" />
            Recent Activity
          </h3>
          <button className="text-sm text-accent-400 hover:text-accent-300 transition-colors">
            View All
          </button>
        </div>
        
        <div className="space-y-4">
          {activity.map((item, idx) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: idx * 0.05 }}
              className="flex items-start gap-4 p-3 rounded-lg hover:bg-primary-700/50 transition-colors"
            >
              <div className={`p-2 rounded-lg ${
                item.type === 'document' ? 'bg-blue-500/20 text-blue-400' :
                item.type === 'client' ? 'bg-green-500/20 text-green-400' :
                'bg-purple-500/20 text-purple-400'
              }`}>
                {getActivityIcon(item.type)}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-white">{item.action}</span>
                  <span className="text-xs text-primary-500">•</span>
                  <span className="text-xs text-primary-400">{item.user}</span>
                </div>
                <p className="text-sm text-primary-300 truncate">{item.description}</p>
              </div>
              
              <span className="text-xs text-primary-500 whitespace-nowrap">
                {formatTimeAgo(item.timestamp)}
              </span>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <QuickAction
          icon={<FileText className="w-5 h-5" />}
          label="New Document"
          onClick={() => {}}
        />
        <QuickAction
          icon={<Users className="w-5 h-5" />}
          label="Add Client"
          onClick={() => {}}
        />
        <QuickAction
          icon={<Briefcase className="w-5 h-5" />}
          label="New Matter"
          onClick={() => {}}
        />
        <QuickAction
          icon={<Calendar className="w-5 h-5" />}
          label="View Calendar"
          onClick={() => {}}
        />
      </div>
    </div>
  );
}

// Stat Card Component
interface StatCardProps {
  title: string;
  value: number;
  change?: number;
  changeLabel?: string;
  subtitle?: string;
  icon: React.ReactNode;
  color: 'blue' | 'green' | 'purple' | 'orange';
}

function StatCard({ title, value, change, changeLabel, subtitle, icon, color }: StatCardProps) {
  const colorClasses = {
    blue: 'bg-blue-500/20 text-blue-400',
    green: 'bg-green-500/20 text-green-400',
    purple: 'bg-purple-500/20 text-purple-400',
    orange: 'bg-orange-500/20 text-orange-400',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-primary-800/50 rounded-xl border border-primary-700 p-6"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-primary-400">{title}</p>
          <p className="text-3xl font-bold text-white mt-1">{value.toLocaleString()}</p>
          
          {change !== undefined && (
            <div className="flex items-center gap-1 mt-2 text-sm">
              <ArrowUpRight className="w-4 h-4 text-green-400" />
              <span className="text-green-400">{change}</span>
              <span className="text-primary-500">{changeLabel}</span>
            </div>
          )}
          
          {subtitle && (
            <p className="text-sm text-primary-500 mt-2">{subtitle}</p>
          )}
        </div>
        
        <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
          {icon}
        </div>
      </div>
    </motion.div>
  );
}

// Quick Action Button
interface QuickActionProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}

function QuickAction({ icon, label, onClick }: QuickActionProps) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-3 p-4 bg-primary-800/50 border border-primary-700 rounded-xl hover:bg-primary-700/50 hover:border-accent-500/50 transition-all group"
    >
      <div className="p-2 bg-accent-500/20 text-accent-400 rounded-lg group-hover:bg-accent-500/30 transition-colors">
        {icon}
      </div>
      <span className="text-sm font-medium text-primary-200 group-hover:text-white transition-colors">
        {label}
      </span>
    </button>
  );
}
